import { DatePicker } from '@tractian/ui'
import { useTranslation } from 'react-i18next'

import { Calendar, resolveCalendarLocale } from '@/components/date-picker/calendar'
import { DateService } from '@/services/date/DateService'

type DatePickerAPI = {
  openPopover: () => void
  closePopover: () => void
  focus: () => void
  focusLast: () => void
}

type DayDatePickerProps = {
  value?: string
  onChange: (day: string) => void
  'aria-label'?: string
  isDateDisabled?: (date: Date) => boolean
  pickerRef?: React.RefObject<DatePickerAPI | null>
  /**
   * Date used to seed the visible month when `value` is empty — NOT a real
   * selection. @tractian/ui Calendar derives its visible month internally
   * from `value` and exposes no `defaultMonth`, so the only lever we have is
   * feeding it a synthetic `value`. Parent state (`value ?? ''`) remains
   * untouched; the trigger's clear affordance is hidden in this mode.
   */
  placeholderDate?: string
}

export function DayDatePicker({
  value,
  onChange,
  isDateDisabled,
  pickerRef,
  placeholderDate,
  ...props
}: DayDatePickerProps) {
  const { i18n } = useTranslation()
  const displayFormat = DateService.dayPickerFormat(i18n.language)

  const handleChange = (date: Date | undefined) => {
    onChange(date ? DateService.formatDayWire(date) : '')
  }

  const effectiveValue = value || placeholderDate
  const isPlaceholder = !value && placeholderDate !== undefined

  return (
    <DatePicker
      ref={pickerRef}
      value={effectiveValue ? DateService.parseDay(effectiveValue) : null}
      isDateDisabled={isDateDisabled}
      onChange={handleChange}
    >
      <DatePicker.Trigger aria-label={props['aria-label']}>
        <DatePicker.Value format={displayFormat} allowClear={!isPlaceholder} />
      </DatePicker.Trigger>
      <DatePicker.Portal>
        <DatePicker.Content>
          <DatePicker.Calendar />
        </DatePicker.Content>
      </DatePicker.Portal>
    </DatePicker>
  )
}

type DayRangePickerProps = {
  value?: { from?: string; to?: string }
  onChange: (range: { from?: string; to?: string }) => void
}

export function DayRangePicker({ value, onChange }: DayRangePickerProps) {
  const { i18n } = useTranslation()
  const from = DateService.parseDay(value?.from || '') ?? undefined
  const to = DateService.parseDay(value?.to || '') ?? undefined

  return (
    <Calendar
      mode="range"
      numberOfMonths={2}
      selected={from || to ? { from, to } : undefined}
      defaultMonth={from ?? to}
      locale={resolveCalendarLocale(i18n.language)}
      onSelect={range =>
        onChange({
          ...(range?.from && { from: DateService.formatDayWire(range.from) }),
          ...(range?.to && { to: DateService.formatDayWire(range.to) })
        })
      }
    />
  )
}
